<template>
  <div class="container-fluid mt-4" dir="rtl">
    
    <!-- الترويسة والتبويبات -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="fw-bold text-dark">
        <i class="fas fa-file-contract ms-2 text-primary"></i> تجهيز واعتماد ملفات إعادة التصحيح
      </h4>
      
      <!-- تبديل بين الجاري والمنتهي -->
      <div class="btn-group" role="group">
        <button 
          @click="changeTab('current')" 
          class="btn" 
          :class="currentTab === 'current' ? 'btn-primary' : 'btn-outline-primary'"
        >
          ملفات للتجهيز ({{ currentTotal }})
        </button>
        <button 
          @click="changeTab('finished')" 
          class="btn" 
          :class="currentTab === 'finished' ? 'btn-secondary' : 'btn-outline-secondary'"
        >
          الأرشيف المنتهي
        </button>
      </div>
    </div>

    <!-- البحث -->
    <div class="card shadow-sm mb-4 border-top-primary">
      <div class="card-body">
        <div class="input-group">
          <span class="input-group-text bg-white"><i class="fas fa-search"></i></span>
          <input 
            v-model="search" 
            @input="fetchData" 
            type="text" 
            class="form-control" 
            placeholder="بحث: الرقم السري الجديد / رقم الجلوس / الاسم..." 
          />
        </div>
      </div>
    </div>

    <!-- الجدول الرئيسي -->
    <div class="card shadow-sm">
      <div class="table-responsive">
        <table class="table table-bordered table-hover align-middle text-center mb-0">
          <thead class="bg-dark text-white">
            <tr>
              <th style="width: 10%">الرقم السري</th>
              <th style="width: 20%">بيانات الطالب</th>
              <th style="width: 30%">مواد للمراجعة (بملاحظات)</th>
              <th style="width: 25%">مواد حفظت (بدون ملاحظات)</th>
              <th v-if="currentTab === 'current'" style="width: 15%">الإجراء</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in appeals.data" :key="row.id">
              
              <!-- الرقم السري (المهم) -->
              <td class="bg-light">
                <h3 class="fw-bold text-danger mb-0">{{ row.file_secret_number }}</h3>
                <!-- <small class="text-muted">Secret No</small> -->
              </td>

              <!-- بيانات الطالب -->
              <td class="text-start">
                <div class="fw-bold">{{ row.student_name }}</div>
                <div class="text-muted small">جلوس: {{ row.Seating_No }}</div>
              </td>

              <!-- المواد التي لها ملاحظات (ستعاد تصحيحها) -->
              <td class="bg-success-subtle text-start">
                <div class="fw-bold text-success mb-1 border-bottom border-success pb-1">
                   العدد: {{ row.remarks_count }}
                </div>
                <div class="d-flex flex-wrap gap-1">
                  <span 
                    v-for="sub in getActiveSubjects(row.subjects)" 
                    :key="sub.id" 
                    class="badge bg-success text-white shadow-sm"
                  >
                    {{ sub.subject_name }}
                  </span>
                </div>
              </td>

              <!-- المواد التي لم يكتب فيها ملاحظات (مرفوضة) -->
              <td class="bg-secondary-subtle text-start opacity-75">
                <div class="text-muted small mb-1 border-bottom pb-1">
                   العدد: {{ row.subjects_count - row.remarks_count }}
                </div>
                <div class="d-flex flex-wrap gap-1">
                  <span 
                    v-for="sub in getRejectedSubjects(row.subjects)" 
                    :key="sub.id" 
                    class="badge bg-secondary text-white"
                  >
                    {{ sub.subject_name }}
                  </span>
                </div>
              </td>

              <!-- الأزرار -->
              <td v-if="currentTab === 'current'">
                <div class="d-grid gap-2">
                  <button 
                    @click="finalizeAppeal(row)" 
                    class="btn btn-primary fw-bold btn-sm"
                    :disabled="row.processing"
                  >
                    <span v-if="row.processing" class="spinner-border spinner-border-sm"></span>
                    <span v-else><i class="fas fa-paper-plane ms-1"></i> اعتماد وإرسال</span>
                  </button>
                  
                  <button @click="printLabel(row)" class="btn btn-outline-dark btn-sm">
                    <i class="fas fa-print ms-1"></i> تيكت الغلاف
                  </button>
                </div>
              </td>
            </tr>

            <tr v-if="appeals.data && appeals.data.length === 0">
              <td colspan="5" class="py-5 text-muted">لا توجد ملفات للعرض.</td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <!-- Pagination -->
      <div class="card-footer bg-white d-flex justify-content-center" v-if="appeals.last_page > 1">
         <!-- استخدم مكون التصفح الخاص بك هنا -->
         <nav aria-label="Page navigation">
            <ul class="pagination mb-0">
                <li class="page-item" :class="{disabled: !appeals.prev_page_url}">
                    <button class="page-link" @click="changePage(appeals.current_page - 1)">سابق</button>
                </li>
                <li class="page-item disabled"><span class="page-link">{{ appeals.current_page }} / {{ appeals.last_page }}</span></li>
                <li class="page-item" :class="{disabled: !appeals.next_page_url}">
                    <button class="page-link" @click="changePage(appeals.current_page + 1)">تالي</button>
                </li>
            </ul>
         </nav>
      </div>
    </div>

    <!-- تيكت الطباعة المخفي (للطباعة فقط) -->
    <div id="labelPrintArea" class="d-none">
        <div v-if="printData" class="label-container" style="text-align: center; border: 2px solid black; padding: 10px; width: 300px;">
            <h1 style="font-size: 60px; margin: 0;">{{ printData.file_secret_number }}</h1>
            <hr>
            <h3>{{ printData.student?.name }}</h3>
            <h4>المواد: <span v-for="s in getActiveSubjects(printData.subjects)">{{ s.subject_name }}، </span></h4>
        </div>
    </div>

  </div>
</template>

<script>

import _ from 'lodash';
import api from '@/api/axios';

export default {
  name: "FinalAppealMonitor",
  data() {
    return {
      loading: false,
      search: "",
      appeals: {}, // Laravel Pagination Object
      currentTab: 'current', // current | finished
      currentTotal: 0,
      printData: null // لتخزين بيانات التيكت المراد طباعته
    };
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    // جلب البيانات
    fetchData: _.debounce(async function(page = 1) {
      this.loading = true;
      try {
        const res = await api.get('/admin/appeals/finalization', {
            params: { 
                search: this.search,
                status: this.currentTab,
                page: page
            }
        });
        this.appeals = res.data;
        if(this.currentTab === 'current') {
            this.currentTotal = res.data.total;
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    }, 500),

    // تغيير التبويب
    changeTab(tab) {
        this.currentTab = tab;
        this.fetchData(); // إعادة تحميل
    },

    changePage(page) {
        if(page > 0 && page <= this.appeals.last_page) {
            this.fetchData(page);
        }
    },

    // مساعد لفلترة المواد في العرض
    getActiveSubjects(subjects) {
        return subjects.filter(s => s.status === 'active' || s.has_remarks == true);
    },
    getRejectedSubjects(subjects) {
        return subjects.filter(s => s.status === 'rejected' || s.has_remarks == false);
    },

    // اعتماد الملف
    async finalizeAppeal(row) {
        if(!confirm(`هل أنت متأكد من اعتماد الملف السري رقم (${row.file_secret_number}) وإرساله للتصحيح؟`)) return;

        this.$set(row, 'processing', true);
        try {
            await api.post(`/admin/appeals/${row.id}/finalize`);
            
            // إخفاء الصف من القائمة الحالية
            this.appeals.data = this.appeals.data.filter(item => item.id !== row.id);
            this.currentTotal--;
            alert("تم الاعتماد بنجاح");
        } catch (error) {
            alert("حدث خطأ");
        } finally {
            this.$set(row, 'processing', false);
        }
    },

    // طباعة التيكت
    printLabel(row) {
        this.printData = row;
        // ننتظر تحديث الـ DOM ثم نطبع
        this.$nextTick(() => {
            const printContent = document.getElementById('labelPrintArea').innerHTML;
            const win = window.open('', '', 'height=500,width=500');
            win.document.write('<html><head><title>Print Label</title></head><body>');
            win.document.write(printContent);
            win.document.write('</body></html>');
            win.document.close();
            win.print();
        });
    }
  }
};
</script>

<style scoped>
.bg-success-subtle { background-color: #d1e7dd; }
.bg-secondary-subtle { background-color: #e2e3e5; }
.border-top-primary { border-top: 4px solid #0d6efd; }
</style>